import { registerPlugin, Capacitor } from "@capacitor/core";
import { nativePlatform, storeLog, type NativeBillingBridge, type StorePlatform } from "@/lib/store-billing";

/**
 * Adapts the Capacitor `InquiraBilling` plugin (see `native/`) to the
 * `window.InquiraNative` contract the app already consumes. Installing the
 * shim here means the wrapper only implements native StoreKit / Play calls and
 * the web layer keeps a single, typed entry point.
 *
 * Every call is recorded so `/subscription-status` can show real-device
 * diagnostics (last payload, last sync time, errors).
 */

interface CapacitorPluginShape {
  purchase(options: { productId: string; obfuscatedAccountId?: string }): Promise<Record<string, unknown>>;
  restore?(): Promise<{ purchases?: Array<Record<string, unknown>> }>;
  manageSubscriptions?(): Promise<void>;
  introOfferEligibility?(options: { productId: string }): Promise<{ eligible?: boolean }>;
  productInfo?(options: { productId: string }): Promise<Record<string, unknown>>;
}

export interface BridgeCallLog {
  method: string;
  at: string;
  ok: boolean;
  detail: string;
}

const callLog: BridgeCallLog[] = [];

function record(method: string, ok: boolean, payload: unknown) {
  callLog.unshift({
    method,
    at: new Date().toISOString(),
    ok,
    detail: (() => {
      try {
        return typeof payload === "string" ? payload : JSON.stringify(payload);
      } catch {
        return String(payload);
      }
    })(),
  });
  callLog.splice(12);
}

/** Most recent bridge calls, newest first — diagnostics only. */
export function bridgeCallLog(): BridgeCallLog[] {
  return [...callLog];
}

let registeredPlugin: CapacitorPluginShape | null = null;

function capacitorPlugin(): CapacitorPluginShape | null {
  if (typeof window === "undefined") return null;
  if (!Capacitor.isPluginAvailable("InquiraBilling")) return null;
  if (!registeredPlugin) {
    registeredPlugin = registerPlugin<CapacitorPluginShape>("InquiraBilling");
  }
  return registeredPlugin;
}

/** Platform the wrapper is running on, used to tag receipts. */
function platform(): StorePlatform {
  const cap = typeof window !== "undefined"
    ? (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor
    : undefined;
  const name = cap?.getPlatform?.() ?? "";
  if (name === "android") return "google";
  if (name === "ios") return "apple";
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent) ? "google" : "apple";
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function buildBridge(plugin: CapacitorPluginShape): NativeBillingBridge {
  return {
    async purchase(productId) {
      // Android only: tag the Play purchase with the signed-in account so the
      // server (and real-time notifications) can resolve its owner.
      let obfuscatedAccountId: string | undefined;
      if (platform() === "google") {
        try {
          const { supabase } = await import("@/integrations/supabase/client");
          obfuscatedAccountId = (await supabase.auth.getSession()).data.session?.user.id;
        } catch {
          /* purchase still links via the signed-in server call */
        }
      }
      const raw = await plugin.purchase(obfuscatedAccountId ? { productId, obfuscatedAccountId } : { productId });
      record("purchase", true, { productId: raw["productId"], expiresDate: raw["expiresDate"] });
      return {
        platform: (str(raw["platform"]) as StorePlatform) ?? platform(),
        receipt: str(raw["receipt"]) ?? "",
        productId: str(raw["productId"]) ?? productId,
      };
    },
    async restore() {
      if (!plugin.restore) return [];
      const result = await plugin.restore();
      const purchases = result?.purchases ?? [];
      record("restore", true, { count: purchases.length });
      return purchases.map((p) => ({
        platform: (str(p["platform"]) as StorePlatform) ?? platform(),
        receipt: str(p["receipt"]) ?? "",
        productId: str(p["productId"]) ?? "",
      }));
    },
    async manageSubscriptions() {
      if (!plugin.manageSubscriptions) throw new Error("unsupported");
      await plugin.manageSubscriptions();
      record("manageSubscriptions", true, "opened");
    },
    async introOfferEligibility(productId) {
      if (!plugin.introOfferEligibility) throw new Error("unsupported");
      try {
        const result = await plugin.introOfferEligibility({ productId });
        const eligible = Boolean(result?.eligible);
        record("introOfferEligibility", true, { productId, eligible });
        return eligible;
      } catch (e) {
        record("introOfferEligibility", false, e instanceof Error ? e.message : String(e));
        throw e;
      }
    },
    async productInfo(productId) {
      if (!plugin.productInfo) throw new Error("unsupported");
      const raw = await plugin.productInfo({ productId });
      const offer = raw["introOffer"] as Record<string, unknown> | null | undefined;
      const info = {
        productId: str(raw["productId"]) ?? productId,
        displayPrice: str(raw["displayPrice"]),
        introOffer: offer
          ? {
              periodUnit: str(offer["periodUnit"]),
              periodCount: typeof offer["periodCount"] === "number" ? offer["periodCount"] : undefined,
              isFreeTrial: Boolean(offer["isFreeTrial"]),
            }
          : null,
        /** Apple-verified expiry when the account already owns the product. */
        storeExpiresAt: str(raw["expiresDate"]) ?? null,
      };
      record("productInfo", true, info);
      return info;
    },
  };
}

/**
 * Installs `window.InquiraNative` from the Capacitor plugin. Safe to call on
 * every mount: it never overwrites a bridge the wrapper injected itself.
 *
 * The Capacitor plugin object can appear a few ticks after first paint, so a
 * failed first lookup keeps retrying briefly instead of concluding the build
 * ships without StoreKit. When it never appears we record `bridge_missing` so
 * `/subscription-status` can say so explicitly.
 */
export function installNativeBridge(retryMs = 6000): boolean {
  if (typeof window === "undefined") return false;
  if (window.InquiraNative && typeof window.InquiraNative.purchase === "function") return true;
  const plugin = capacitorPlugin();
  if (!plugin) {
    if (retryMs > 0) {
      const started = Date.now();
      const tick = () => {
        if (installNativeBridge(0)) return;
        if (Date.now() - started >= retryMs) {
          record("install", false, "InquiraBilling plugin not registered in this build");
          storeLog("bridge_missing", { platform: nativePlatform() });
          return;
        }
        setTimeout(tick, 200);
      };
      setTimeout(tick, 200);
    }
    return false;
  }
  window.InquiraNative = buildBridge(plugin);
  record("install", true, "InquiraNative installed from Capacitor plugin");
  return true;
}

/** True when the Capacitor `InquiraBilling` plugin is registered in this build. */
export function nativeBillingPluginPresent(): boolean {
  return capacitorPlugin() !== null;
}
